/**
 * Back Office links in Bekki answers.
 *
 * The model never writes URLs. It writes Markdown links with a semantic `bo:`
 * reference, e.g. `[Tour 4835](bo:tour/<tourId>?depotId=<depotId>)`:
 *
 *   bo:<kind>/<arg>/…?depotId=…&internalDepotId=…&tenantId=…
 *
 * - `kind` + the number of args select a rule in `LINK_RULES`.
 * - Depot pages name their depot with `depotId` (numeric, Bobby's `dcId`) or
 *   `internalDepotId` (Data Gateway), else the selected depot is used.
 * - Tenant pages take `tenantId`, else the selected tenant is used.
 *
 * A reference becomes a link only if it parses, the depot is in the user's own
 * `/depots` list (or the user can access the tenant), and the user holds the
 * page's read actions there. Anything else renders as its plain label. The
 * backoffice-mcp system prompt must use this same format.
 */
import { computed } from 'vue';

import type { RouteLocationRaw } from 'vue-router';
import { useDisplay } from 'vuetify';

import { isDate, isId, isUuid } from '@/composables/api/bekki/pageContext';
import type { Depot } from '@/composables/api/userDepots/types';
import constants from '@/config/constants';
import { logAppError } from '@/config/sentryConfig';
import dayjs from '@/plugins/dayjs';
import { hugeIconToSvgString } from '@/plugins/vuetify/icons/hugeIcons';
import router from '@/router';
import useBekkiChatStore from '@/stores/bekkiChat';
import { usePermissionsStore } from '@/stores/permissions';
import useScopesStore from '@/stores/scopes';
import useUserDepotsStore from '@/stores/userDepots';
import { escapeHtml } from '@/utils/escapeHtml';

type RouteParams = Record<string, string>;

interface LinkRuleBase {
  actions: string[];
  args: ((value: string) => boolean)[];
  params?: (args: string[]) => RouteParams;
  icon: string;
}

interface DepotLinkRule extends LinkRuleBase {
  level: 'depot';
  routes: { depot: string; subcontractor: string };
}

interface TenantLinkRule extends LinkRuleBase {
  level: 'tenant';
  route: string;
}

type LinkRule = DepotLinkRule | TenantLinkRule;

interface Link {
  rule: LinkRule;
  args: string[];
  depotId: string | undefined;
  internalDepotId: string | undefined;
  tenantId: string | undefined;
}

interface LinkScope {
  isSubcontractor: boolean;
  depots: Depot[];
  selectedDepot: Depot | null;
  selectedTenantId: string | null;
  hasTenantAccess: (tenantId: string) => boolean;
  hasDepotAction: (action: string, path: string) => boolean;
  hasTenantAction: (action: string, tenantId: string) => boolean;
}

const LINK_SCHEME = 'bo:';
const LINK_PATTERN = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;
const PARTIAL_LINK_PATTERN = /\[([^\]\n]*)(\]\([^)\s]*)?$/;
const DEPOT_ID_PATTERN = /^\d{1,10}$/;

const linkIcon = (name: string): string =>
  hugeIconToSvgString(name, 'bekki-link__icon');

/**
 * One rule per link kind, keyed by `<kind>/<number of args>`. A rule lists
 * the arg validators, the read actions the user needs, the route(s) to open
 * and the route params built from the args.
 */
const LINK_RULES: Record<string, LinkRule> = {
  'depot/0': {
    level: 'depot',
    actions: ['r:depot-landing-page'],
    args: [],
    routes: { depot: 'depotLanding', subcontractor: 'subcontractorLanding' },
    icon: linkIcon('huge:dashboard-square')
  },
  'live-status/0': {
    level: 'depot',
    actions: ['r:tour'],
    args: [],
    routes: {
      depot: 'liveStatusTours',
      subcontractor: 'subcontractorLiveStatusTours'
    },
    icon: linkIcon('huge:live-streaming')
  },
  'tour/1': {
    level: 'depot',
    actions: ['r:tour', 'r:tour-details'],
    args: [isUuid],
    routes: { depot: 'tourView', subcontractor: 'subcontractorTourView' },
    params: ([tourId = '']) => ({ tourId }),
    icon: linkIcon('huge:delivery-tracking')
  },
  'tour-history/1': {
    level: 'depot',
    actions: ['r:tour-location-history'],
    args: [isUuid],
    routes: {
      depot: 'tourDrivenHistoryV2',
      subcontractor: 'subcontractorTourDrivenHistoryV2'
    },
    params: ([tourId = '']) => ({ tourId }),
    icon: linkIcon('huge:route')
  },
  'tour-performance/0': {
    level: 'depot',
    actions: ['r:tour-perf'],
    args: [],
    routes: {
      depot: 'tourPerformance',
      subcontractor: 'subcontractorTourPerformance'
    },
    icon: linkIcon('huge:truck-delivery')
  },
  'tour-performance/3': {
    level: 'depot',
    actions: ['r:tour-perf'],
    args: [isId, isDate, isDate],
    routes: {
      depot: 'tourPerformanceDetails',
      subcontractor: 'subcontractorTourPerformanceDetails'
    },
    params: ([tour = '', from = '', to = '']) => ({
      tour,
      dates: `${from}_${to}`
    }),
    icon: linkIcon('huge:truck-delivery')
  },
  'activities/1': {
    level: 'depot',
    actions: ['r:tour-activity'],
    args: [isDate],
    routes: {
      depot: 'dailyActivities',
      subcontractor: 'subcontractorDailyActivities'
    },
    params: ([date = '']) => ({ date: dayjs(date).format('YYYYMMDD') }),
    icon: linkIcon('huge:chart')
  },
  'parcel/1': {
    level: 'tenant',
    actions: ['r:parcel-finder'],
    args: [isId],
    route: 'parcelDetails',
    params: ([parcelId = '']) => ({ parcelId }),
    icon: linkIcon('huge:package-search')
  },
  'job/1': {
    level: 'tenant',
    actions: ['r:dgw-pages'],
    args: [isId],
    route: 'dataGatewayJobDetailsContainer',
    params: ([jobId = '']) => ({ jobId }),
    icon: linkIcon('huge:quiz')
  },
  'health/0': {
    level: 'tenant',
    actions: ['r:health-status'],
    args: [],
    route: 'healthStatus',
    icon: linkIcon('huge:health')
  }
};

const parseLink = (href: string): Link | null => {
  if (!href.startsWith(LINK_SCHEME)) return null;

  const [path = '', query = ''] = href.slice(LINK_SCHEME.length).split('?');
  const [kind = '', ...args] = path.split('/');
  const rule = LINK_RULES[`${kind}/${args.length}`];
  if (!rule?.args.every((isValid, index) => isValid(args[index] ?? ''))) {
    return null;
  }

  const searchParams = new URLSearchParams(query);
  const depotId = searchParams.get('depotId') ?? undefined;
  const internalDepotId = searchParams.get('internalDepotId') ?? undefined;
  const tenantId = searchParams.get('tenantId') ?? undefined;
  if (
    (depotId !== undefined && !DEPOT_ID_PATTERN.test(depotId)) ||
    (internalDepotId !== undefined && !isId(internalDepotId)) ||
    (tenantId !== undefined && !isId(tenantId))
  ) {
    return null;
  }

  return { rule, args, depotId, internalDepotId, tenantId };
};

const findDepot = (
  { depotId, internalDepotId }: Link,
  { depots, selectedDepot }: LinkScope
): Depot | null => {
  if (depotId) return depots.find(({ id }) => id === Number(depotId)) ?? null;
  if (internalDepotId) {
    return (
      depots.find((depot) => depot.internalDepotId === internalDepotId) ?? null
    );
  }
  return selectedDepot;
};

/**
 * Resolves a parsed link to a route the user may open, or `null`.
 * Depot rules open the subcontractor twin page for subcontractor users;
 * tenant rules never resolve for them.
 */
const getLocation = (link: Link, scope: LinkScope): RouteLocationRaw | null => {
  const { rule, args } = link;
  const params = rule.params?.(args) ?? {};

  if (rule.level === 'tenant') {
    const tenantId = link.tenantId ?? scope.selectedTenantId;
    return !scope.isSubcontractor &&
      tenantId &&
      scope.hasTenantAccess(tenantId) &&
      rule.actions.every((action) => scope.hasTenantAction(action, tenantId))
      ? { name: rule.route, params: { ...params, tenantId } }
      : null;
  }

  const depot = findDepot(link, scope);
  return depot &&
    rule.actions.every((action) => scope.hasDepotAction(action, depot.path))
    ? {
        name: rule.routes[scope.isSubcontractor ? 'subcontractor' : 'depot'],
        params: {
          ...params,
          tenantId: depot.tenant.id,
          externalDepotId: depot.externalDepotId
        }
      }
    : null;
};

/**
 * Replaces every link, including a half-received one at the end, with its
 * label. Used while an answer streams, so a link only becomes clickable once
 * the answer is complete.
 */
export const toBekkiLinkLabels = (text: string): string =>
  text.replace(LINK_PATTERN, '$1').replace(PARTIAL_LINK_PATTERN, '$1');

let depotsRequest: Promise<void> | null = null;

/**
 * Renders `bo:` links in an answer and handles clicks on them.
 *
 * - `renderLinks(html)` takes the answer after HTML-escaping and Markdown
 *   formatting (before sanitizing) and turns resolvable links into pills.
 * - `onLinkClick(event)` is the click handler for the message element: a plain
 *   left click navigates in the app (closing the chat on small screens),
 *   modifier and middle clicks fall through to the browser.
 */
export const useBekkiLinks = () => {
  const scopesStore = useScopesStore();
  const permissionsStore = usePermissionsStore();
  const userDepotsStore = useUserDepotsStore();
  const bekkiChatStore = useBekkiChatStore();
  const { mdAndUp } = useDisplay();

  if (!userDepotsStore.depots.length && !depotsRequest) {
    depotsRequest = userDepotsStore.loadDepots().catch((error) => {
      depotsRequest = null;
      logAppError({
        error,
        functionName: 'useBekkiLinks',
        section: 'bekki',
        level: 'warning'
      });
    });
  }

  const scope = computed((): LinkScope => {
    const { type, data } = scopesStore.selectedScope;
    return {
      isSubcontractor: type === constants.scopeTypes.SUBCONTRACTOR,
      depots: userDepotsStore.depots,
      selectedDepot: data,
      selectedTenantId:
        data?.tenant.id ?? permissionsStore.selectedTenant ?? null,
      hasTenantAccess: permissionsStore.hasTenantAccess,
      hasDepotAction: (action, path) =>
        permissionsStore.hasActionForPath(action, path.replaceAll('-', '_')),
      hasTenantAction: permissionsStore.hasActionForTenantPath
    };
  });

  const renderLinks = (html: string): string =>
    html.replace(LINK_PATTERN, (_match, label: string, href: string) => {
      const link = parseLink(href.replaceAll('&amp;', '&'));
      const location = link && getLocation(link, scope.value);
      if (!link || !location) return label;

      const { href: resolvedHref, fullPath } = router.resolve(location);
      const leadingText = /^[^\s<]*/.exec(label)?.[0] ?? '';
      return `<a href="${escapeHtml(resolvedHref)}" class="bekki-link text-primary font-weight-medium text-decoration-none rounded-sm" data-cc="bekki-link" data-bekki-link="${escapeHtml(fullPath)}"><span class="bekki-link__label"><span class="text-no-wrap">${link.rule.icon}${leadingText}</span>${label.slice(leadingText.length)}</span></a>`;
    });

  const onLinkClick = (event: MouseEvent): void => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const path = (event.target as Element | null)?.closest<HTMLElement>(
      '[data-bekki-link]'
    )?.dataset.bekkiLink;
    if (!path) return;

    event.preventDefault();
    router.push(path);
    if (!mdAndUp.value) bekkiChatStore.close();
  };

  return { renderLinks, onLinkClick };
};
